import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Hammer, House, Kanban, Table, GearSix, SignOut, Sparkle, Clock, List, X, User as UserIcon, UsersThree, Lifebuoy } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";

const baseNavItems = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/discover", label: "Lead Discovery", icon: Sparkle, testid: "nav-discover" },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-pipeline" },
  { to: "/leads", label: "All Leads", icon: Table, testid: "nav-leads" },
  { to: "/saved-searches", label: "Saved Searches", icon: Clock, testid: "nav-saved" },
  { to: "/profile", label: "Profile", icon: UserIcon, testid: "nav-profile" },
  { to: "/support", label: "Support", icon: Lifebuoy, testid: "nav-support" },
];

const adminNavItems = [
  { to: "/admin/users", label: "Users", icon: UsersThree, testid: "nav-admin-users" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

function EnvBadge() {
  const url = process.env.REACT_APP_BACKEND_URL || "";
  const isPreview = url.includes(".preview.emergentagent.com");
  const isLive = !isPreview && url.includes("emergent.host");
  if (!isPreview && !isLive) return null;
  return (
    <div
      data-testid="env-badge"
      className={`fixed top-3 right-3 z-40 font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm border shadow-sm pointer-events-none ${
        isPreview
          ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
          : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
      }`}
      title={isPreview ? "Preview / dev environment — changes here won't affect your live site" : "Live production site"}
    >
      {isPreview ? "PREVIEW" : "LIVE"}
    </div>
  );
}

function SidebarBody({ user, onLogout, onNavigate }) {
  const navItems = user?.role === "admin"
    ? [...baseNavItems, ...adminNavItems]
    : baseNavItems;
  return (
    <>
      <div className="px-5 py-5 border-b border-zinc-900 flex items-center gap-2.5">
        <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center forge-pulse">
          <Hammer size={18} weight="bold" className="text-zinc-950" />
        </div>
        <div>
          <div className="font-display font-semibold text-base tracking-tight leading-none">ClickForge</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-orange-500 mt-1">Lead Agent</div>
        </div>
      </div>
      <nav className="flex-1 px-2 py-4 flex flex-col gap-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all ${
                isActive
                  ? "bg-orange-600/10 text-orange-400 border border-orange-600/30"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border border-transparent"
              }`
            }
          >
            <item.icon size={18} weight="regular" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-zinc-900 p-3">
        <div className="px-2 py-2 text-xs">
          <div className="text-zinc-300 truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-zinc-500 truncate font-mono text-[10px]" data-testid="sidebar-user-email">{user?.email}</div>
        </div>
        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="sm"
          className="w-full justify-start text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
          onClick={onLogout}
        >
          <SignOut size={16} className="mr-2" /> Log out
        </Button>
      </div>
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      {/* Desktop sidebar (md+) */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-64 shrink-0 border-r border-zinc-900 bg-zinc-950/80 backdrop-blur-sm flex-col"
      >
        <SidebarBody user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <>
          <div
            data-testid="mobile-nav-backdrop"
            className="md:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            data-testid="mobile-sidebar"
            className="md:hidden fixed left-0 top-0 h-full w-64 bg-zinc-950 border-r border-zinc-900 z-50 flex flex-col animate-in slide-in-from-left duration-200"
          >
            <SidebarBody user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <EnvBadge />
        <TrialBanner />
        {/* Mobile topbar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-900">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 text-zinc-300 hover:text-orange-400"
            aria-label="Open navigation"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-600 rounded-sm flex items-center justify-center">
              <Hammer size={14} weight="bold" className="text-zinc-950" />
            </div>
            <div className="font-display font-semibold text-sm">ClickForge</div>
          </div>
          <div className="w-8" />
        </div>
        {children}
      </main>
    </div>
  );
}
